import { Notice } from "@/components/Notice";
import { useToast } from "@/components/ui/Toast";
import {
	bypassCodeQueryKeys,
	useCreateBypassCodeMutation,
	useGenerateBypassCodeMutation,
	useGetActiveBypassCodeQuery,
	useUpdateBypassCodeMutation,
} from "@/query/bypassCodeQuery";
import { Box, Flex, ModalBody, ModalFooter, Spinner } from "@chakra-ui/react";
import * as bypassCodeApi from "js/api/bypassCodeApi";
import NuSingleSelect from "js/components/NuSingleSelect";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClasswizeModal } from "../../../components/ui/Modal";
import { PrimaryButton } from "../shared/button/PrimaryButton";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { useCountdown } from "@/hooks/useCountdown";

interface BypassModalProps {
	onClose: () => void;
}

const BypassModal: React.FC<BypassModalProps> = ({ onClose }) => {
	const { t } = useTranslation();
	const [duration, setDuration] = useState<string>("600");
	const [isBypassActive, setIsBypassActive] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const activeCodeQuery = useGetActiveBypassCodeQuery({
		onSuccess: onActiveCodeQuerySuccess,
		onError: onActiveCodeQueryError,
		cacheTime: 0, // disable stale-while-revalidate behaviour to avoid flickering on subsequent modal opens
	});
	const queryClient = useQueryClient();
	const generateCodeMutation = useGenerateBypassCodeMutation({ onError: onGenerateCodeMutationError });
	const createBypassCodeMutation = useCreateBypassCodeMutation();
	const updateBypassCodeMutation = useUpdateBypassCodeMutation();

	const classroomPolicyStore = useClassroomPolicyStore((state) => ({
		setBypassActiveUntil: state.setBypassActiveUntil,
	}));

	const toast = useToast();

	const durationOptions = useMemo(
		() => [
			{ value: "600", text: t("bypassModal.duration10Minutes") },
			{ value: "900", text: t("bypassModal.duration15Minutes") },
			{ value: "1200", text: t("bypassModal.duration20Minutes") },
			{ value: "1800", text: t("bypassModal.duration30Minutes") },
			{ value: "2700", text: t("bypassModal.duration45Minutes") },
			{ value: "3600", text: t("bypassModal.duration1Hour") },
			{ value: "7200", text: t("bypassModal.duration2Hours") },
			{ value: "10800", text: t("bypassModal.duration3Hours") },
		],
		[t]
	);

	function onActiveCodeQuerySuccess(data: bypassCodeApi.GetActiveBypassCodeResponse) {
		if (!data.result) {
			generateCodeMutation.mutate();
		} else {
			setIsBypassActive(true);
		}
	}

	function onActiveCodeQueryError(error: unknown) {
		console.error("Failed to get active bypass code:", error);
		toast.errorToast({
			title: t("bypassModal.toastLoadErrorTitle"),
			description: t("bypassModal.toastLoadErrorDescription"),
		});
		onClose();
	}

	function onGenerateCodeMutationError(error: unknown) {
		console.error("Failed to generate bypass code:", error);
		toast.errorToast({
			title: t("bypassModal.toastLoadErrorTitle"),
			description: t("bypassModal.toastLoadErrorDescription"),
		});
		onClose();
	}

	const reset = useCallback(() => {
		setDuration("600");
		setIsBypassActive(false);
		activeCodeQuery.refetch();
	}, [activeCodeQuery]);

	const startBypass = useCallback(async () => {
		setError(null);

		const code = generateCodeMutation.data?.result;
		if (!code) {
			console.error("Invalid state: No code generated", { code });
			return;
		}

		const activeUntil = moment().unix() + parseInt(duration);
		const creationTime = moment().unix();

		const bypass: bypassCodeApi.CreateBypassCodeRequest = {
			code,
			creation_time: creationTime,
			expiry_time: activeUntil,
		};

		let createResponse: bypassCodeApi.CreateBypassCodeResponse;
		try {
			createResponse = await createBypassCodeMutation.mutateAsync(bypass);
		} catch (error) {
			console.error("Failed to start bypass code:", error);
			setError(t("bypassModal.errorStartFailed"));
			return;
		}

		queryClient.setQueryData<bypassCodeApi.GetActiveBypassCodeResponse>(bypassCodeQueryKeys.active, {
			result: createResponse.result,
		});
		setIsBypassActive(true);
		classroomPolicyStore.setBypassActiveUntil(createResponse.result.expiry_time);
	}, [generateCodeMutation.data?.result, duration, createBypassCodeMutation, queryClient, classroomPolicyStore, t]);

	const stopBypass = useCallback(async () => {
		setError(null);

		const current = activeCodeQuery.data?.result;
		if (!current) {
			console.error("Invalid state: No active bypass code found", { active: current });
			return;
		}

		const now = moment().unix();
		const bypass: bypassCodeApi.UpdateBypassCodeRequest = {
			id: current.id,
			code: current.code,
			creation_time: current.creation_time,
			expiry_time: now,
		};

		try {
			await updateBypassCodeMutation.mutateAsync(bypass);
		} catch (error) {
			console.error("Failed to stop bypass code:", error);
			setError(t("bypassModal.errorStopFailed"));
			return;
		}

		classroomPolicyStore.setBypassActiveUntil(now);
		onClose();
	}, [activeCodeQuery.data?.result, updateBypassCodeMutation, classroomPolicyStore, onClose, t]);

	const close = useCallback(() => {
		onClose();
	}, [onClose]);

	const changeBypassDuration = useCallback((value: string) => {
		setDuration(value);
	}, []);

	const renderButton = () => {
		if (isBypassActive) {
			return (
				<PrimaryButton
					className="mui-flat-button"
					data-testid="stop-bypass-button"
					disabled={updateBypassCodeMutation.isLoading}
					onClick={stopBypass}
				>
					{updateBypassCodeMutation.isLoading ? (
						<span>
							<Spinner mr={2} data-testid="stop-loading-spinner" />
							{t("bypassModal.buttonStopping")}
						</span>
					) : (
						t("bypassModal.buttonStop")
					)}
				</PrimaryButton>
			);
		} else {
			return (
				<PrimaryButton
					className="mui-flat-button"
					data-testid="start-bypass-button"
					disabled={createBypassCodeMutation.isLoading}
					onClick={startBypass}
				>
					{createBypassCodeMutation.isLoading ? (
						<span>
							<Spinner mr={2} data-testid="start-loading-spinner" />
							{t("bypassModal.buttonStarting")}
						</span>
					) : (
						t("bypassModal.buttonStart")
					)}
				</PrimaryButton>
			);
		}
	};

	const renderTimeSelection = () => {
		const bypassActiveUntil = activeCodeQuery.data?.result?.expiry_time ?? 0;

		if (isBypassActive) {
			return (
				<>
					<Box className="BypassModal_title">{t("bypassModal.timeLeft")}</Box>
					<div className="BypassModal_time">
						<BypassModalCountdown expiryTimeUnix={bypassActiveUntil} onExpiry={reset} />
					</div>
				</>
			);
		}

		return (
			<>
				<Box className="BypassModal_title">{t("bypassModal.bypassDuration")}</Box>
				<NuSingleSelect
					allItems={durationOptions}
					selectedItem={duration}
					searchPlaceholder={t("bypassModal.selectDurationPlaceholder")}
					onChange={changeBypassDuration}
				/>
			</>
		);
	};

	const renderCode = () => {
		const code = activeCodeQuery.data?.result?.code ?? generateCodeMutation.data?.result ?? "----";
		return (
			<>
				<div className="BypassModal_title">{t("bypassModal.sectionBypassCode")}</div>
				<div className="BypassModal_code">{code}</div>
			</>
		);
	};

	const initialLoading = activeCodeQuery.isFetching || generateCodeMutation.isLoading;

	return (
		<ClasswizeModal title={t("bypassModal.modalTitle")} isOpen={true} onClose={close} dataTestId="bypass-modal" size="md">
			{initialLoading ? (
				<ModalBody>
					<Flex justifyContent="center" alignItems="center" h="200px">
						<Spinner size="xl" data-testid="loading-spinner" />
					</Flex>
				</ModalBody>
			) : (
				<>
					<ModalBody>
						{error && <Notice status="error" children={error} mb={4} />}
						<Flex direction="row" gap={16} align="flex-start">
							<Box flex="1" minW={0}>
								{renderTimeSelection()}
							</Box>
							<Box flex="1" minW={0}>
								{renderCode()}
							</Box>
						</Flex>
					</ModalBody>
					<ModalFooter>{renderButton()}</ModalFooter>
				</>
			)}
		</ClasswizeModal>
	);
};

interface BypassModalCountdownProps {
	expiryTimeUnix: number;
	onExpiry?: () => void;
}

const BypassModalCountdown: React.FC<BypassModalCountdownProps> = ({ expiryTimeUnix, onExpiry }) => {
	// total seconds to countdown
	const totalSeconds = useMemo(() => {
		return expiryTimeUnix - moment().unix();
	}, [expiryTimeUnix]);

	const [count, { startCountdown }] = useCountdown({
		countStart: totalSeconds,
		intervalMs: 1000,
	});

	useEffect(() => {
		startCountdown();
	}, [startCountdown]);

	useEffect(() => {
		if (count <= 0) {
			onExpiry?.();
		}
	}, [count, onExpiry]);

	const countdownTime = useMemo(() => {
		return moment.utc(count * 1000).format("HH:mm:ss");
	}, [count]);

	return <div>{countdownTime}</div>;
};

export default BypassModal;
